import { NostrEvent, NostrFilter, NostrRelay, NostrRequest } from '../../types/nostr';

export class NostrRelayManager {
  private connections: Map<string, WebSocket> = new Map();
  private subscriptions: Map<string, Set<string>> = new Map();
  private messageHandlers: Map<string, (event: NostrEvent) => void> = new Map();
  private relays: Map<string, NostrRelay> = new Map();

  // Same relay list as the Swift app
  private readonly defaultRelays = [
    "wss://relay.damus.io",
    "wss://nos.lol",
    "wss://relay.primal.net",
    "wss://offchain.pub",
    "wss://nostr21.com"
  ];

  constructor() {
    // Initialize relay states
    this.defaultRelays.forEach(url => {
      this.relays.set(url, {
        url,
        isConnected: false,
        messagesSent: 0,
        messagesReceived: 0
      });
    });
  }

  async connect(): Promise<void> {
    console.log('🌐 Connecting to Nostr relays...');

    const connectionPromises = this.defaultRelays.map(url => this.connectToRelay(url));
    await Promise.allSettled(connectionPromises);
  }

  private async connectToRelay(url: string): Promise<void> {
    if (this.connections.has(url)) {
      return;
    }

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log(`✅ Connected to Nostr relay: ${url}`);
        this.updateRelayStatus(url, { isConnected: true, lastConnectedAt: new Date() });
        this.connections.set(url, ws);
        resolve();
      };

      ws.onmessage = (event) => {
        this.handleMessage(event.data, url);
      };

      ws.onerror = (error) => {
        console.error(`❌ Failed to connect to ${url}:`, error);
        this.updateRelayStatus(url, {
          isConnected: false,
          lastError: new Error('WebSocket connection failed')
        });
        reject(error);
      };

      ws.onclose = (event) => {
        console.log(`🔌 Disconnected from ${url}`);
        this.connections.delete(url);
        this.updateRelayStatus(url, { isConnected: false });

        // Attempt reconnection with exponential backoff
        if (!event.wasClean) {
          this.scheduleReconnection(url);
        }
      };
    });
  }

  private handleMessage(data: string, relayUrl: string): void {
    try {
      const message: any[] = JSON.parse(data);
      const [type, ...args] = message;

      // Update relay stats
      const relay = this.relays.get(relayUrl);
      if (relay) {
        relay.messagesReceived++;
      }

      switch (type) {
        case 'EVENT':
          const [subId, eventData] = args;
          const event = this.parseNostrEvent(eventData);

          // Only log non-gift-wrap events to reduce noise
          if (event.kind !== 1059) {
            console.log(`📥 Event kind=${event.kind} id=${event.id?.slice(0, 16)}… relay=${relayUrl}`);
          }

          // Call handler
          const handler = this.messageHandlers.get(subId);
          if (handler) {
            handler(event);
          } else {
            console.warn(`⚠️ No handler for subscription ${subId}`);
          }
          break;

        case 'OK':
          const [eventId, success, reason] = args;
          console.log(`${success ? '✅' : '❌'} Event ${eventId}: ${reason}`);
          break;

        case 'EOSE':
          // End of stored events - can be used for pagination
          break;

        case 'NOTICE':
          const [notice] = args;
          console.log(`📢 Notice from ${relayUrl}: ${notice}`);
          break;

        default:
          console.log(`Unknown message type: ${type}`);
      }
    } catch (error) {
      console.error('Failed to parse Nostr message:', error);
    }
  }

  private parseNostrEvent(data: any): NostrEvent {
    return {
      id: data[0],
      pubkey: data[1],
      created_at: data[2],
      kind: data[3],
      tags: data[4] || [],
      content: data[5],
      sig: data[6]
    };
  }

  subscribe(
    filter: NostrFilter,
    handler: (event: NostrEvent) => void,
    relayUrls?: string[]
  ): string {
    const subId = Math.random().toString(36).substring(7);
    this.messageHandlers.set(subId, handler);

    console.log(`📡 Subscribing to Nostr filter id=${subId}`);

    const request: NostrRequest = {
      type: 'REQ',
      id: subId,
      filters: [filter]
    };

    const targets = relayUrls || this.defaultRelays;
    targets.forEach(url => {
      const ws = this.connections.get(url);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(request));

        // Track subscription per relay
        if (!this.subscriptions.has(url)) {
          this.subscriptions.set(url, new Set());
        }
        this.subscriptions.get(url)!.add(subId);
      }
    });

    return subId;
  }

  unsubscribe(subscriptionId: string): void {
    this.messageHandlers.delete(subscriptionId);

    const request: NostrRequest = {
      type: 'CLOSE',
      id: subscriptionId
    };

    // Send unsubscribe to all relays
    this.connections.forEach((ws, url) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(request));

        // Remove from relay's subscriptions
        const relaySubs = this.subscriptions.get(url);
        if (relaySubs) {
          relaySubs.delete(subscriptionId);
        }
      }
    });
  }

  async sendEvent(event: NostrEvent, relayUrls?: string[]): Promise<void> {
    const request: NostrRequest = {
      type: 'EVENT',
      event
    };

    const targets = relayUrls || this.defaultRelays;
    const sendPromises = targets.map(async (url) => {
      const ws = this.connections.get(url);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(request));

        // Update relay stats
        const relay = this.relays.get(url);
        if (relay) {
          relay.messagesSent++;
        }
      }
    });

    await Promise.allSettled(sendPromises);
  }

  disconnect(): void {
    this.connections.forEach((ws, url) => {
      ws.close(1000, 'Client disconnecting');
    });
    this.connections.clear();
    this.subscriptions.clear();
    this.messageHandlers.clear();
  }

  getRelays(): NostrRelay[] {
    return Array.from(this.relays.values());
  }

  private updateRelayStatus(url: string, updates: Partial<NostrRelay>): void {
    const relay = this.relays.get(url);
    if (relay) {
      Object.assign(relay, updates);
    }
  }

  private scheduleReconnection(url: string): void {
    const relay = this.relays.get(url);
    if (!relay) return;

    relay.reconnectAttempts = (relay.reconnectAttempts || 0) + 1;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const backoffMs = Math.min(1000 * Math.pow(2, relay.reconnectAttempts - 1), 30000);

    console.log(`⏰ Scheduling reconnection to ${url} in ${backoffMs}ms`);

    setTimeout(() => {
      // Only reconnect if we're not already connected
      if (!this.connections.has(url)) {
        this.connectToRelay(url);
      }
    }, backoffMs);
  }
}
